// ============================================================================
// olvasatlan.js — melyik szobában van olvasatlan üzenet.
//
// A szerver minden új üzenetnél felírja a szobára, mikor és kitől jött az
// utolsó üzenet (rooms/{id}.lastMessageAt, lastMessageBy). A kliens
// eszközönként megjegyzi, szobánként meddig olvasott; ha az utolsó üzenet
// ennél újabb, és nem ő írta, a szoba olvasatlan.
//
// DÖNTÉSEK:
//  - Eszközönként (localStorage), nem fiókonként: így nem kell minden
//    szobanyitásnál a szerverre írni. Következmény: ami az egyik eszközön
//    elolvasott, a másikon még olvasatlannak látszhat.
//  - Első találkozáskor (új eszköz, új szoba) a szoba OLVASOTTNAK számít:
//    különben egy új telefonon minden szoba villogna a régi üzenetek miatt.
//  - A feljegyzés felhasználónként külön kulcson él (közös eszközön sem
//    keveredik), és hibás/tiltott tárolónál sem dob hibát.
//
// Tiszta logika, a tárolót paraméterként kapja - Node-ban tesztelhető.
// ============================================================================

const KULCS_ELOTAG = "lineage0_olvasott_";

function tarolo(storage) {
  if (storage) return storage;
  try {
    return typeof localStorage !== "undefined" ? localStorage : null;
  } catch (e) {
    return null;
  }
}

// A beolvasott feljegyzés a memóriában (2026-09-30, audit).
//
// FONTOS, AUDITBÓL: a localStorage SZINKRON, a fő szálat blokkolja. Ez a
// feljegyzés minden beérkező üzenetnél kétszer is beolvasódott és JSON-ként
// újra elemződött (szobaOlvasottnakJelolese + olvasatlanFrissitese), tehát egy
// 20 részes hosszú üzenet 40 darab szinkron tárolóolvasást és -elemzést
// jelentett - pont akkor, amikor a lista is épp frissül.
//
// A gyorsítótár CSAK az alapértelmezett (böngészős) tárolóra vonatkozik: ha a
// hívó saját tárolót ad át (tesztek), mindig az olvasódik, változatlanul. Másik
// lap írását a "storage" esemény üríti, így a több lapon nyitott alkalmazás sem
// dolgozik elavult adatból.
let gyorsitotarUid = null;
let gyorsitotarAdat = null;

export function uritsdAzOlvasottGyorsitotarat() {
  gyorsitotarUid = null;
  gyorsitotarAdat = null;
}

try {
  if (typeof window !== "undefined" && window.addEventListener) {
    window.addEventListener("storage", (e) => {
      if (!e || !e.key || e.key.startsWith(KULCS_ELOTAG)) uritsdAzOlvasottGyorsitotarat();
    });
  }
} catch (e) {
  // Nem kritikus: gyorsítótár-ürítés nélkül is helyesen működik egy lapon.
}

function beolvas(s, uid) {
  try {
    const adat = JSON.parse(s.getItem(KULCS_ELOTAG + uid) || "{}");
    return adat && typeof adat === "object" && !Array.isArray(adat) ? adat : {};
  } catch (e) {
    return {};
  }
}

/** A felhasználó szobánkénti "eddig olvasott" időpontjai: { roomId: ms }. */
export function olvasottak(uid, storage) {
  const s = tarolo(storage);
  if (!s || !uid) return {};
  if (storage) return beolvas(s, uid); // injektált tároló: sosem gyorsítótárazunk
  if (gyorsitotarUid === uid && gyorsitotarAdat) return { ...gyorsitotarAdat };
  const adat = beolvas(s, uid);
  gyorsitotarUid = uid;
  gyorsitotarAdat = adat;
  return { ...adat };
}

function ment(uid, adat, storage) {
  const s = tarolo(storage);
  if (!s || !uid) return;
  if (!storage) {
    gyorsitotarUid = uid;
    gyorsitotarAdat = { ...adat };
  }
  try {
    s.setItem(KULCS_ELOTAG + uid, JSON.stringify(adat));
  } catch (e) {
    // Megtelt vagy tiltott tároló: az olvasatlan-jelzés pontatlan lesz, más nem.
  }
}

/**
 * A szobalista feldolgozása: az első ízben látott szobák olvasottnak
 * számítanak (alapvonal). Visszaadja az olvasatlan szobák azonosítóit.
 *
 * @param {string} uid
 * @param {{roomId: string, lastMessageAt: number|null, lastMessageBy: string|null}[]} szobak
 * @returns {string[]}
 */
export function olvasatlanSzobak(uid, szobak, storage) {
  const adat = olvasottak(uid, storage);
  let valtozott = false;
  const eredmeny = [];
  for (const sz of szobak) {
    if (!sz || !sz.roomId) continue;
    const utolso = typeof sz.lastMessageAt === "number" ? sz.lastMessageAt : 0;
    if (typeof adat[sz.roomId] !== "number") {
      adat[sz.roomId] = utolso; // alapvonal: amit első ízben látunk, az "olvasott"
      valtozott = true;
      continue;
    }
    if (utolso > adat[sz.roomId] && sz.lastMessageBy !== uid) eredmeny.push(sz.roomId);
  }
  if (valtozott) ment(uid, adat, storage);
  return eredmeny;
}

/** Egy szoba olvasottnak jelölése a megadott időpontig (csak előre lép). */
export function olvasottnakJelol(uid, roomId, idopont, storage) {
  if (!uid || !roomId || typeof idopont !== "number") return;
  const adat = olvasottak(uid, storage);
  if (typeof adat[roomId] === "number" && adat[roomId] >= idopont) return;
  adat[roomId] = idopont;
  ment(uid, adat, storage);
}
